/**
 * THE MATCH DRAFT'S SHAPES: the firm administrator's saved answers to the move
 * matcher, one per legacy client, kept between sittings (decision 041; spec
 * docs/specs/bms/2026-10-24-001-move-matcher-keeps-progress.md §3, §7.1, cut
 * to its v1). Stored in `insurance_broker_bms.org_migration_match_choices`.
 *
 * BROWSER-SAFE: `effect` only. The view group's contract references these
 * schemas and the settings slice imports the contract by path. A contract
 * cannot name the matcher's barrel, so the three words are spelled here; the
 * matcher's `DraftChoice` (`matcher/domain/draft-states.ts`) holds the same
 * three, and a unit test keeps the two equal.
 *
 * NOTHING MOVES FROM THE DRAFT. It only fills the agreement page's answers;
 * the administrator still agrees once over the resolution the page builds.
 *
 * Ids, closed words, a hash and times only: no name or value the firm typed.
 */
import { Schema as S } from "effect"

export const MATCH_DRAFT_CHOICES = ["join", "apart", "setAside", "archive", "together"] as const
export const MatchDraftChoice = S.Literal(...MATCH_DRAFT_CHOICES).annotations({ identifier: "CutoverMatchDraftChoice" })
export type MatchDraftChoice = typeof MatchDraftChoice.Type

/**
 * How an answer was given (build plan 2026-11-03-002): one row at a time, to a
 * filtered set, carried from the firm's last agreement, or as a group's answer.
 * The matcher's `ANSWERED_VIA` holds the same words; a unit test keeps them equal.
 */
export const MATCH_ANSWERED_VIA = ["row", "filter", "agreement", "copies", "twice", "crm", "possible", "none"] as const
export const AnsweredVia = S.Literal(...MATCH_ANSWERED_VIA).annotations({ identifier: "CutoverAnsweredVia" })
export type AnsweredVia = typeof AnsweredVia.Type

/** 32 lower-case hex characters: the matcher's `rowFingerprintOf` of the row as the screen showed it. */
export const SeenFingerprint = S.String.pipe(S.pattern(/^[0-9a-f]{32}$/)).annotations({ identifier: "CutoverSeenFingerprint" })

/** One saved answer. */
export const MatchDraftRow = S.Struct({
  id: S.UUID,
  legacyId: S.UUID,
  choice: MatchDraftChoice,
  /** The Cova CRM client a `join` names; null for every other answer. */
  bookId: S.NullOr(S.UUID),
  seenFingerprint: S.String,
  /**
   * Who answered: the administrator's Clerk id, or the Cova staff member's on
   * the firm's instruction. NULL on the firm's own lane when Cova staff gave
   * the answer (`decidedOnBehalf`), so a staff member's id never reaches the
   * firm's browser (build plan 2026-11-03-002, B3); the staff lane returns it.
   */
  decidedByUserId: S.NullOr(S.String),
  /** ISO 8601. */
  decidedAt: S.String,
  /** `together`: the keeper, another legacy client of the firm. */
  keeperLegacyId: S.NullOr(S.UUID),
  /** `archive`: the Cova CRM client it duplicates, recorded only. */
  duplicateOfBookId: S.NullOr(S.UUID),
  /** A `join`: the client's archived flag when the answer was saved; null when not recorded (answers saved before v8.89). */
  bookArchivedWhenSaved: S.NullOr(S.Boolean),
  answeredVia: AnsweredVia,
  /** Saved by Cova staff on the firm's written instruction. */
  decidedOnBehalf: S.Boolean,
}).annotations({ identifier: "CutoverMatchDraftRow" })
export type MatchDraftRow = typeof MatchDraftRow.Type

/**
 * The firm's whole draft: up to one row per legacy client (about 3,500 for a
 * large firm; one read, no page, build plan Task 12 states the size), and the
 * archived flag of every Cova CRM client a saved join names, so the page can
 * read a search pick against today's flag as the server does.
 */
export const MatchDraft = S.Struct({
  rows: S.Array(MatchDraftRow),
  bookClients: S.Array(S.Struct({ bookId: S.UUID, archived: S.Boolean })),
}).annotations({ identifier: "CutoverMatchDraft" })
export type MatchDraft = typeof MatchDraft.Type

/**
 * What the page sends for one answer. `bookId` is required for `join` and
 * refused otherwise; `keeperLegacyId` is required for `together` and refused
 * otherwise; `duplicateOfBookId` only on `archive`. The client's archived flag
 * for a join is read by the server, never sent.
 */
export const SaveMatchChoicePayload = S.Struct({
  choice: MatchDraftChoice,
  bookId: S.NullOr(S.UUID),
  seenFingerprint: SeenFingerprint,
  keeperLegacyId: S.optional(S.NullOr(S.UUID)),
  duplicateOfBookId: S.optional(S.NullOr(S.UUID)),
  /** Absent reads as `row`. */
  answeredVia: S.optional(AnsweredVia),
}).annotations({ identifier: "CutoverSaveMatchChoicePayload" })

/** The most answers one batch carries: a large firm's whole group in one request. */
export const MATCH_BATCH_MAX = 4000

/**
 * Many answers in one request (a group answer, "answer the shown"), written in
 * one transaction under the draft lock. `replace` is "Replace them" on the
 * confirm (N-B2): without it, an answer that is not a row answer never
 * replaces an existing `agreement` or `filter` answer; a `row` answer is never
 * replaced by a batch (`mayOverwrite`).
 */
export const SaveMatchChoicesPayload = S.Struct({
  answers: S.Array(S.Struct({ legacyId: S.UUID, ...SaveMatchChoicePayload.fields })).pipe(
    S.minItems(1),
    S.maxItems(MATCH_BATCH_MAX),
  ),
  replace: S.optional(S.Boolean),
}).annotations({ identifier: "CutoverSaveMatchChoicesPayload" })
export type SaveMatchChoicesPayload = typeof SaveMatchChoicesPayload.Type

/** What a batch did: answers written, and answers it kept because `mayOverwrite` refused. */
export const SavedMatchChoices = S.Struct({
  saved: S.Int,
  kept: S.Int,
}).annotations({ identifier: "CutoverSavedMatchChoices" })
export type SavedMatchChoices = typeof SavedMatchChoices.Type

/**
 * What "Start from the last agreement" did (build plan §4.1, round 1 S2):
 * every legacy client of the proposal in exactly one bucket, so the parts sum
 * to the proposal's rows. It never touches a saved answer.
 */
export const PrefillReport = S.Struct({
  /** The agreement carried, or null when the firm never agreed (nothing written). */
  agreementId: S.NullOr(S.UUID),
  /** ISO 8601, or null. */
  agreedAt: S.NullOr(S.String),
  /** Written now. */
  carried: S.Int,
  /** Already answered as the agreement did. */
  alreadyAnswered: S.Int,
  /** Already answered differently: kept. */
  keptOwn: S.Int,
  /** A saved answer that is stale or "decide later": kept, for the person to look at. */
  staleLeft: S.Int,
  /** Locked by Cova (certain) and the agreement agrees. */
  locked: S.Int,
  /** Locked by Cova, and the agreement named another client. */
  lockedNow: S.Int,
  /** A client added since the agreement. */
  notInAgreement: S.Int,
  /** The agreement's join names a client no longer in the firm. */
  clientGone: S.Int,
}).annotations({ identifier: "CutoverPrefillReport" })
export type PrefillReport = typeof PrefillReport.Type
export type SaveMatchChoicePayload = typeof SaveMatchChoicePayload.Type

export const ClearedMatchChoice = S.Struct({ legacyId: S.UUID }).annotations({ identifier: "CutoverClearedMatchChoice" })
export type ClearedMatchChoice = typeof ClearedMatchChoice.Type

/** The group words of `MATCH_ANSWERED_VIA`: an answer one of the review's group cards gave. */
export const MATCH_GROUP_WORDS = ["copies", "twice", "crm", "possible", "none"] as const
export const MatchGroupWord = S.Literal(...MATCH_GROUP_WORDS).annotations({ identifier: "CutoverMatchGroupWord" })
export type MatchGroupWord = typeof MatchGroupWord.Type

/**
 * "I'll go through them" (build plan 2026-11-03-002, P5; review round 1,
 * finding 1): one group's own answers taken back in one request. Only an
 * answer saved with that group's word is removed, so an answer given row by
 * row, to a filter or carried from the agreement is never touched, whatever
 * the ids name.
 */
export const ClearGroupAnswersPayload = S.Struct({
  answeredVia: MatchGroupWord,
  legacyIds: S.Array(S.UUID).pipe(S.minItems(1), S.maxItems(MATCH_BATCH_MAX)),
}).annotations({ identifier: "CutoverClearGroupAnswersPayload" })
export type ClearGroupAnswersPayload = typeof ClearGroupAnswersPayload.Type

/** How many of the group's answers were taken back. */
export const ClearedGroupAnswers = S.Struct({ cleared: S.Int }).annotations({ identifier: "CutoverClearedGroupAnswers" })
export type ClearedGroupAnswers = typeof ClearedGroupAnswers.Type

/** What a draft write says while the firm cannot take an agreement. */
export const DRAFT_CLOSED_MESSAGE =
  "This firm cannot take an agreement now: it already has one, or its move has started, so its matches cannot be changed."
